import { S } from '@mobily/ts-belt';
import { recordPageReady } from '@stitch-fix/client-analytics-reporter';
import { BusinessLine } from '../../types/definitions/vendor/graphql-schema.d';
import { usePaginationParams } from '../../utils/hooks/usePaginationParams';
import PaginationLinks from '../PaginationLinks';
import Recommendations from '../Recommendations/Recommendations';
import { useGetUngatedCategoryRecsPaginatedQuery, GetUngatedCategoryRecsPaginatedQuery } from './getUngatedCategoryRecsPaginated';
import { getMediaImage, getMediaObjectFromOriginalSrc } from '../Recommendations/ProductRecommendations/ShopProductCards';
export const RECOMMENDATIONS_PER_PAGE = 60;

// Since navigating to subsequent pages of recommendations does not cause a page reload,
// we should only call `recordPageReady` when a client initially navigates to the category listing page.
let hasPageLoaded = false;
const handleRecordPageReady = (dataResponse: GetUngatedCategoryRecsPaginatedQuery) => {
  const shopProducts = dataResponse.shopContent?.categoryPage?.category?.shopProductsByPage?.nodes;
  if (shopProducts && !hasPageLoaded) {
    hasPageLoaded = true;
    const rumImages = shopProducts
    // Only load the top 4 products per mobile above the fold
    .slice(0, 4)
    // Get the images we are going to display based on index
    .map(getMediaImage)
    // Load only valid media paths
    .map(media => {
      const {
        mediaPath
      } = getMediaObjectFromOriginalSrc(media?.originalSrc || '');
      return mediaPath;
    });
    recordPageReady({
      resources: rumImages
    });
  }
};
const PaginatedRecommendations = ({
  categoryId,
  categorySetId,
  businessLine
}: {
  categoryId: string;
  categorySetId: string;
  businessLine: BusinessLine;
}) => {
  const {
    paginationParams
  } = usePaginationParams();
  const {
    data,
    loading,
    error
  } = useGetUngatedCategoryRecsPaginatedQuery({
    variables: {
      /**
       * Business Line passed to query 'MENS' | 'WOMENS'
       */
      businessLine,
      id: categoryId || '',
      limit: RECOMMENDATIONS_PER_PAGE,
      page: paginationParams.page
    },
    context: {
      operationTelemetry: {
        tags: {
          business_line: businessLine,
          /**
           * We always pass business line into the ungated query, but not the gated query.
           * If there is a business line, then we know these are ungated recommendations.
           * When we unify the apps this value can reflect those changes.
           */
          gated: !businessLine
        }
      }
    },
    // The `usePaginationParams` hook sets the `page` query param to 0
    // when `page` is an invalid string so we want to skip the graphql request
    // to guard against possible DDoS attacks
    skip: S.isEmpty(categoryId) || paginationParams.page === 0,
    onCompleted: dataResponse => {
      handleRecordPageReady(dataResponse);
    }
  });
  const shopProductsByPage = data?.shopContent?.categoryPage?.category?.shopProductsByPage;
  const totalPageCount = shopProductsByPage?.pageCount || 1;
  return <>
      <Recommendations categoryId={categoryId} categorySetId={categorySetId} shopProducts={shopProductsByPage?.nodes} loading={loading} error={error} />
      <PaginationLinks pageCount={totalPageCount} buttonGroupLabel="Pagination for recommendations" />
    </>;
};
export default PaginatedRecommendations;